import { Observable, Subscriber } from 'rxjs'
import { areElementsVisible, isElementVisible } from '../../DOM/DOMUtils'

const MAX_WAIT_TIME: number = 10000
const WAIT_INTERVAL: number = 200

/**
 * Check if the element is visible every 200ms for 10 seconds
 *
 * @param element - HTMLElement - The element to check
 *
 * @returns Promise<boolean> - True if the element is visible
 */
export const checkIfElementIsVisible = (
    element: HTMLElement,
    allowedTopLevelElementSelector: string[]
): Promise<boolean> =>
    new Promise((resolve: (value: boolean) => void): void => {
        let waitTime: number = 0

        // First check if the element is already visible.
        if (isElementVisible(element, ...allowedTopLevelElementSelector)) {
            resolve(true)
        } else {
            // If not visible, check every 200ms for 10 seconds.
            const interval: number = window.setInterval((): void => {
                if (isElementVisible(element, ...allowedTopLevelElementSelector)) {
                    clearInterval(interval)
                    resolve(true)
                } else if (waitTime >= MAX_WAIT_TIME) {
                    clearInterval(interval)
                    resolve(false)
                } else {
                    waitTime += WAIT_INTERVAL
                }
            }, WAIT_INTERVAL)
        }
    })

/**
 * Wait for the elements to be visible
 *
 * @param selector - string - The selector to use to find the elements
 * @param allowedTopLevelElementSelector - string[] - The selectors of the elements that are allowed to be the top level element
 *
 * @returns Observable<HTMLElement[]> - The elements that are visible
 */
export const $waitForElements = (
    selector: string,
    ...allowedTopLevelElementSelector: string[]
): Observable<HTMLElement[]> =>
    new Observable((subscriber: Subscriber<HTMLElement[]>) => {
        const elements: OnlyNullable<NodeListOf<HTMLElement>> = document.querySelectorAll(selector)

        if (
            elements &&
            elements.length > 0 &&
            areElementsVisible(Array.from(elements), ...allowedTopLevelElementSelector)
        ) {
            subscriber.next(Array.from(elements))
            subscriber.complete()
        } else if (elements && elements.length > 0) {
            const options: IntersectionObserverInit = {
                root: null,
                rootMargin: '0px',
                threshold: 1.0
            }

            const obs: IntersectionObserver = new IntersectionObserver(
                (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
                    entries.forEach((entry: IntersectionObserverEntry): void => {
                        checkIfElementIsVisible(entry.target as HTMLElement, allowedTopLevelElementSelector).then(
                            (isVisible: boolean): void => {
                                if (isVisible) {
                                    subscriber.next(Array.from(elements))
                                    subscriber.complete()
                                    observer.disconnect()
                                }
                            }
                        )
                    })
                },
                options
            )

            elements.forEach((element: HTMLElement): void => obs.observe(element))

            subscriber.next([])
        } else {
            const observer: MutationObserver = new MutationObserver((mutations: MutationRecord[]): void => {
                mutations.forEach((): void => {
                    const els: OnlyNullable<HTMLElement[]> = Array.from(document.querySelectorAll(selector))

                    if (els && els.length > 0 && areElementsVisible(els, ...allowedTopLevelElementSelector)) {
                        subscriber.next(els)
                        subscriber.complete()
                        observer.disconnect()
                    }
                })
            })

            observer.observe(document.body, {
                childList: true,
                subtree: true
            })
        }
    })
